import { useState } from "react";
import { Reveal, Overline } from "./Reveal";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";

const STEPS = [
  { no: "01", t: "Consultation", d: "Tell us the occasion, style and colours you want." },
  { no: "02", t: "Fabric & Measure", d: "Pick a fabric and we'll take your measurements." },
  { no: "03", t: "The Making", d: "Our tailors stitch and hand-finish your kurta." },
  { no: "04", t: "Fitting & Delivery", d: "Final fitting at our store. Adjusted perfectly to you." },
];

const initial = { name: "", phone: "", email: "", interest: "", message: "" };

export default function CustomStudio() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please provide your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", form);
      toast.success("Inquiry received. Our atelier will reach out shortly.");
      setForm(initial);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="studio" data-testid="custom-studio" className="bg-charcoal grain text-cream py-24 md:py-36 relative overflow-hidden">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16">
        <div>
          <Reveal>
            <Overline>Custom Tailoring</Overline>
            <h2 className="mt-5 font-display font-black uppercase tracking-normal text-cream text-4xl md:text-6xl leading-[0.95]">
              Made just <span className="text-toga">for you.</span>
            </h2>
            <p className="mt-6 text-cream/70 font-light text-lg max-w-md leading-relaxed">
              Have us make something custom — from first consultation to final fitting. Tell us where to begin.
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 gap-px bg-cream/10 border border-cream/10">
            {STEPS.map((s, i) => (
              <Reveal key={s.no} delay={i * 0.08}>
                <div className="bg-charcoal p-6 h-full">
                  <span className="font-display font-black text-toga text-3xl">{s.no}</span>
                  <h4 className="mt-3 font-display uppercase tracking-tight text-cream text-lg font-bold">{s.t}</h4>
                  <p className="mt-2 text-sm text-cream/55 font-light">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.15}>
          <form onSubmit={submit} data-testid="custom-inquiry-form" className="border border-cream/15 bg-charcoal/40 p-8 md:p-12">
            <h3 className="font-display uppercase tracking-tight text-cream text-2xl font-bold">Custom Tailoring Inquiry</h3>
            <div className="mt-8 space-y-7">
              <input data-testid="lead-name" className="line-input !text-cream !border-cream/25 placeholder:!text-cream/40" placeholder="Full Name *" value={form.name} onChange={set("name")} />
              <input data-testid="lead-phone" className="line-input !text-cream !border-cream/25 placeholder:!text-cream/40" placeholder="Phone / WhatsApp *" value={form.phone} onChange={set("phone")} />
              <input data-testid="lead-email" className="line-input !text-cream !border-cream/25 placeholder:!text-cream/40" placeholder="Email (optional)" value={form.email} onChange={set("email")} />
              <input data-testid="lead-interest" className="line-input !text-cream !border-cream/25 placeholder:!text-cream/40" placeholder="What are you looking for? (e.g. Wedding Sherwani)" value={form.interest} onChange={set("interest")} />
              <textarea data-testid="lead-message" rows={3} className="line-input !text-cream !border-cream/25 placeholder:!text-cream/40 resize-none" placeholder="Tell us more…" value={form.message} onChange={set("message")} />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="lead-submit"
              className="mt-10 w-full bg-toga text-white px-8 py-4 text-xs uppercase tracking-[0.18em] font-bold hover:bg-cream hover:text-charcoal transition-colors duration-300 disabled:opacity-60"
            >
              {submitting ? "Sending…" : "Submit Inquiry"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
